import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MapPin, ArrowDownRight } from "lucide-react";
import { MaskLine } from "@/lib/motion";
import { IMAGES } from "@/lib/images";
import { useScrollTo } from "@/lib/useScrollTo";

export const Hero = () => {
  const scrollTo = useScrollTo();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPortrait = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const yGlow = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-[#0F0E0E]">
      {/* clean dark background with soft coral glow */}
      <motion.div
        style={{ y: yGlow }}
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#FF6B6B]/15 blur-[140px] pointer-events-none"
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen grid lg:grid-cols-12 gap-10 items-center pt-28 pb-16">
        {/* LEFT — text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-10 h-px bg-[#FF6B6B]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B6B]">
              Treningi personalne 1:1 dla kobiet 30+
            </span>
          </motion.div>

          <h1 className="font-display font-light text-[#F5F2F0] leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-8xl">
            <MaskLine delay={0.25}>Zmieść się</MaskLine>
            <MaskLine delay={0.4}>
              w <span className="italic text-[#FF6B6B]">ulubioną</span> sukienkę
            </MaskLine>
            <MaskLine delay={0.55}>i pokaż na plaży.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 text-base md:text-lg text-muted max-w-xl leading-relaxed"
          >
            Schudnij zdrowo, poczuj się lżej i odzyskaj pewność siebie. Prowadzę Cię krok po kroku
            na treningach w Zdrofit Warszawa Stegny.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <button
              onClick={() => scrollTo("ai")}
              data-testid="hero-ai-cta"
              className="btn-primary rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] flex items-center gap-2"
            >
              Zobacz swoją przemianę <ArrowDownRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("kontakt")}
              data-testid="hero-contact-cta"
              className="btn-ghost rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em]"
            >
              Umów konsultację
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-12 flex items-center gap-2 text-sm text-muted"
          >
            <MapPin className="w-4 h-4 text-[#FF6B6B]" />
            Zdrofit Warszawa Stegny · ul. Mangalia 4 · Mokotów
          </motion.div>
        </div>

        {/* RIGHT — trainer portrait */}
        <div className="lg:col-span-5">
          <motion.div
            style={{ y: yPortrait }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#FF6B6B]/40 z-0" />
            <div className="relative z-10 overflow-hidden bg-[#1A1718]">
              <img
                src={IMAGES.trainerTop}
                alt="Twoja trenerka personalna"
                className="w-full aspect-[10/7] object-cover object-top"
              />
            </div>
            <div className="absolute z-20 -bottom-5 left-5 right-5 bg-[#0F0E0E]/85 backdrop-blur-xl border border-[#2A2626] px-5 py-4">
              <p className="font-display text-2xl text-[#F5F2F0] leading-none">Twoja trenerka</p>
              <p className="text-xs uppercase tracking-[0.18em] text-[#FF6B6B] mt-1.5">
                Trener personalny · Zdrofit Stegny
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
